import {
  Brain, Search, LayoutGrid, Palette, Users, CheckCircle,
  Target, ShoppingCart, Package, Smartphone, Store, Briefcase,
  Shield, Zap, Crown, ClipboardList, Wand2, Sparkles, ShieldCheck, Plus,
  Shirt, Coffee, BookOpen, MapPin, Gift,
  Sun, Mountain, Focus, Hand, Contrast, Camera, Layers,
} from 'lucide-react';
import type { BrandTeamSessionMode, BrandTeamDirection, MockupStyle } from '../../../services/brandTeamService';

export interface AgentPersona {
  agentKey: string;
  name: string;
  role: string;
  icon: typeof Brain;
  gradientFrom: string;
  gradientTo: string;
}

export const AGENT_PERSONAS: AgentPersona[] = [
  { agentKey: 'brand-strategist', name: 'Sofia', role: 'Brand Strategist', icon: Brain, gradientFrom: 'from-brand-purple', gradientTo: 'to-indigo-600' },
  { agentKey: 'asset-analyst', name: 'Reya', role: 'Asset Analyst', icon: Search, gradientFrom: 'from-blue-500', gradientTo: 'to-brand-blue' },
  { agentKey: 'mockup-planner', name: 'Max', role: 'Mockup Planner', icon: LayoutGrid, gradientFrom: 'from-teal-500', gradientTo: 'to-emerald-500' },
  { agentKey: 'art-director', name: 'Marco', role: 'Art Director', icon: Palette, gradientFrom: 'from-brand-purple', gradientTo: 'to-violet-500' },
  { agentKey: 'brand-manager', name: 'Lena', role: 'Brand Manager', icon: Users, gradientFrom: 'from-brand-purple', gradientTo: 'to-brand-blue' },
  { agentKey: 'qa-reviewer', name: 'Kai', role: 'QA Reviewer', icon: CheckCircle, gradientFrom: 'from-green-500', gradientTo: 'to-emerald-600' },
];

export const MODE_OPTIONS: Array<{ value: BrandTeamSessionMode; label: string; icon: typeof Target; description: string }> = [
  { value: 'full-brand-pack', label: 'Full Brand Pack', icon: Target, description: 'Cross-category planning across core brand surfaces.' },
  { value: 'ecommerce-pack', label: 'E-Commerce Pack', icon: ShoppingCart, description: 'Product-first visuals for online storefronts.' },
  { value: 'packaging-pack', label: 'Packaging Pack', icon: Package, description: 'Packaging-led outputs for labels, bottles, and boxes.' },
  { value: 'social-launch-pack', label: 'Social Launch', icon: Smartphone, description: 'Campaign-ready visuals for launch content.' },
  { value: 'signage-retail-pack', label: 'Signage & Retail', icon: Store, description: 'Large-format surfaces for brand visibility.' },
  { value: 'agency-presentation-pack', label: 'Agency Presentation', icon: Briefcase, description: 'Presentation-friendly coverage for client pitches.' },
  { value: 'apparel-merch-pack', label: 'Apparel & Merch', icon: Shirt, description: 'T-shirts, hoodies, caps, and branded merchandise.' },
  { value: 'corporate-identity-pack', label: 'Corporate Identity', icon: Shield, description: 'Business cards, letterheads, and brand stationery.' },
  { value: 'food-beverage-pack', label: 'Food & Beverage', icon: Coffee, description: 'Labels, menus, cups, and food packaging mockups.' },
  { value: 'book-publishing-pack', label: 'Book & Publishing', icon: BookOpen, description: 'Book covers, magazines, and editorial layouts.' },
  { value: 'event-tradeshow-pack', label: 'Event & Trade Show', icon: MapPin, description: 'Banners, booths, badges, and event collateral.' },
  { value: 'vouchers-gift-pack', label: 'Vouchers & Gifts', icon: Gift, description: 'Gift cards, vouchers, coupons, and gift packaging.' },
];

export const DIRECTION_OPTIONS: Array<{ value: BrandTeamDirection; label: string; icon: typeof Shield; description: string }> = [
  { value: 'safe', label: 'Safe', icon: Shield, description: 'Clean, commercial, low-risk.' },
  { value: 'bold', label: 'Bold', icon: Zap, description: 'High-contrast, assertive.' },
  { value: 'premium', label: 'Premium', icon: Crown, description: 'Elevated, photorealistic.' },
];

export const MOCKUP_STYLE_OPTIONS: Array<{ value: MockupStyle; label: string; icon: typeof Sparkles; description: string }> = [
  { value: 'auto', label: 'Auto (AI Decides)', icon: Sparkles, description: 'AI picks optimal style' },
  { value: 'realism-lifestyle', label: 'Realism & Lifestyle', icon: Sun, description: 'Real people, natural light, everyday environments' },
  { value: 'contextual-scene', label: 'Contextual Scene', icon: Mountain, description: 'User-described scene (cafe, workspace, kitchen)' },
  { value: 'hyper-realistic', label: 'Hyper-Realistic AI', icon: Focus, description: 'Cinematic lighting, depth of field, subtle imperfections' },
  { value: 'human-interaction', label: 'Human Interaction', icon: Hand, description: 'Hands holding, unboxing, person engaging product' },
  { value: 'smart-dynamic', label: 'Smart Dynamic', icon: Wand2, description: 'Auto color palettes, lighting, scaling, props' },
  { value: 'brutalist-editorial', label: 'Brutalist & Editorial', icon: Contrast, description: 'Harsh shadows, bold type, concrete textures' },
  { value: 'studio-clean', label: 'Studio Clean', icon: Camera, description: 'Neutral cyclorama, professional product lighting' },
  { value: 'flat-brand', label: 'Flat Brand Color', icon: Palette, description: 'Solid brand-color background' },
  { value: 'minimal-scandinavian', label: 'Minimal Scandinavian', icon: Layers, description: 'Soft tones, negative space, muted palette' },
];

export const STAGE_ORDER = ['intake', 'brief', 'plan', 'direction', 'production', 'qa'] as const;
export type StageKey = (typeof STAGE_ORDER)[number];

export const STAGE_META: Record<StageKey, { label: string; icon: typeof Plus; description: string }> = {
  intake: { label: 'Intake', icon: Plus, description: 'Assets & context captured' },
  brief: { label: 'Brief', icon: ClipboardList, description: 'Strategy brief generated' },
  plan: { label: 'Plan', icon: LayoutGrid, description: 'Deliverable queue built' },
  direction: { label: 'Art Direction', icon: Wand2, description: 'Visual system defined' },
  production: { label: 'Production', icon: Sparkles, description: 'Mockups generated' },
  qa: { label: 'QA Review', icon: ShieldCheck, description: 'Quality scored' },
};

export function getAgentPersona(agentKey: string): AgentPersona {
  return AGENT_PERSONAS.find((p) => p.agentKey === agentKey) || {
    agentKey, name: agentKey, role: 'Specialist',
    icon: Users, gradientFrom: 'from-gray-400', gradientTo: 'to-gray-500',
  };
}

export function runtimeModeLabel(mode: string): string {
  if (mode === 'anthropic') return 'AI Agents';
  if (mode === 'heuristic-fallback') return 'Heuristic Fallback';
  return 'Standard';
}
